<script setup lang="ts">
import { computed, ref } from 'vue';
import { Github, Scale, Tv } from 'lucide-vue-next';
import OpenSourceLicenseModal from './OpenSourceLicenseModal.vue';
import Icon from './Icon.vue';
import logo from '../assets/fuqiAccount.png';
import hostLogo from '../assets/fuqiHost.png';
import type { OpenSourceLicense } from './shared/openSourceLicense';
import { version } from '../../package.json';

defineEmits<{ navigate: [screen: string] }>();
const props = withDefaults(defineProps<{ realm?: 'account' | 'host' | 'message'; appVersion?: string; licenses?: OpenSourceLicense[] }>(), { realm: 'account' });
const appName = computed(() => props.realm === 'host' ? 'fuqiHost' : props.realm === 'message' ? '传个纸条' : 'fuqiAccount');
const intro = computed(() => props.realm === 'host' ? '给站长的日常管理，安一个舒服的家～' : props.realm === 'message' ? '认真接住每一句话，把消息送到对的人手里。' : '给你的小账号，安一个舒服的家～');
const licenseOpen = ref(false);
const hostUpdates = [
  { icon: 'MessageCircle', title: 'ChatGPT通信器', text: '应急客户端可以提交求助，再通过推送或查询取回回复。平时的工具掉线了，也留一条求助的路～' },
  { icon: 'Activity', title: '请求走到哪，心里有数', text: '查看订阅、请求和回复状态，发送测试请求；投递失败后可以手动重试。' },
  { icon: 'Clock3', title: '调用记录，可以收起来', text: '墨水屏、HA 和 ChatGPT 的最近调用默认收起，刷新后仍保留你的展开选择。' },
];
const features = computed(() => props.realm === 'host' ? [
  { icon: 'ChartNoAxesCombined', title: '网站近况，一眼看清', text: '查看各站点的使用统计，了解大家最近都在玩什么。', screen: 'analytics', action: '看看统计' },
  { icon: 'Users', title: '账号与接入，统一照看', text: '管理平台用户与接入网站，让各个小站顺畅地配合。', screen: 'users', action: '管理用户' },
  { icon: 'Fingerprint', title: '站长的小账号，也要安心', text: '管理通行密钥、登录设备与记录，把管理入口照看好。', screen: 'security', action: '管理通行密钥' },
] : props.realm === 'message' ? [
  { icon: 'MessageCircle', title: '每张纸条，都认真接住', text: '查看收到的纸条，处理并回复，把沟通留在同一个地方。', screen: 'notes', action: '看看纸条' },
  { icon: 'Mail', title: '想说的话，送到对的人', text: '选择消息类别与发送范围，发送消息并查看已经发出的内容。', screen: 'send', action: '发送消息' },
  { icon: 'Users', title: '收件人，安排得明明白白', text: '管理用户、用户组和群组，为不同的消息找到合适的收件人。', screen: 'users', action: '管理用户' },
] : [
  { icon: 'Users', title: '一个账号，认出你', text: '在接入 fuqi 账号的网站之间，带着你的身份继续逛。', screen: 'profile', action: '看看朕的资料' },
  { icon: 'Fingerprint', title: '登录也可以很轻松', text: '密码、通行密钥、身份验证器，把顺手的登录方式安排好。', screen: 'security', action: '管理登录方式' },
  { icon: 'Laptop', title: '谁在线，心里有数', text: '查看登录设备和最近的记录，不认识的设备就让它下线。', screen: 'devices', action: '看看登录设备' },
]);
</script>

<template>
  <div class="account-about" :class="{ 'message-about': realm === 'message' }">
    <header v-if="realm !== 'message'" class="page-heading"><span class="eyebrow">认识一下，这里是</span><h1>关于我们<span class="accent-dot">.</span></h1><p>{{ intro }}</p></header>
    <section class="about-brand-card">
      <span v-if="realm === 'message'" class="about-message-logo"><Icon name="Mail" :size="38" /></span>
      <img v-else :src="realm === 'host' ? hostLogo : logo" :alt="appName" />
      <div><span class="about-kicker">{{ realm === 'host' ? '你的 fuqi 管理中心' : realm === 'message' ? 'fuqiMessager · 你的小小邮局' : '你的 fuqi 账号中心' }}</span><h2>{{ appName }}</h2><p>{{ realm === 'account' ? '登录少费点劲，把时间留给喜欢的事。' : intro }}</p></div>
      <span class="about-version">v{{ appVersion || version }}</span>
    </section>
    <section aria-labelledby="about-features">
      <div class="section-heading"><h2 id="about-features">{{ realm === 'account' ? '小账号，能做这些' : realm === 'host' ? '站长日常，一处照看' : '小小邮局，能做这些' }}</h2><span class="muted">需要的时候，随时回来</span></div>
      <div class="about-feature-grid">
        <article v-for="feature in features" :key="feature.screen" class="about-feature">
          <span class="icon-tile"><Icon :name="feature.icon" /></span><h3>{{ feature.title }}</h3><p>{{ feature.text }}</p>
          <button class="text-button" @click="$emit('navigate', feature.screen)">{{ feature.action }}<Icon name="ArrowRight" :size="16" /></button>
        </article>
      </div>
    </section>
    <section v-if="realm === 'host'" aria-labelledby="about-updates">
      <div class="section-heading"><h2 id="about-updates">最近的更新</h2><span class="muted">v1.3.0 · 2026.10.25</span></div>
      <div class="about-feature-grid">
        <article v-for="update in hostUpdates" :key="update.title" class="about-feature">
          <span class="icon-tile"><Icon :name="update.icon" /></span><h3>{{ update.title }}</h3><p>{{ update.text }}</p>
        </article>
      </div>
      <button class="text-button" type="button" @click="$emit('navigate', 'connectors')">看看连接器<Icon name="ArrowRight" :size="16" /></button>
    </section>
    <slot name="updates" />
    <button class="about-license" type="button" @click="licenseOpen = true"><Scale :size="20" />开源项目许可<Icon name="ChevronRight" :size="18" /></button>
    <section aria-labelledby="about-author">
      <div class="section-heading"><h2 id="about-author">来找 fuqi 玩</h2><span class="muted">代码之外，也有日常</span></div>
      <div class="about-links">
        <a class="bilibili" href="https://space.bilibili.com/1208040550" target="_blank" rel="noreferrer"><Tv :size="24" /><div><strong>Bilibili</strong><p>去 B 站串个门～</p></div><Icon name="ArrowUpRight" :size="18" /></a>
        <a class="github" href="https://github.com/fuqi0125" target="_blank" rel="noreferrer"><Github :size="24" /><div><strong>GitHub</strong><p>看看 fuqi 正在折腾什么</p></div><Icon name="ArrowUpRight" :size="18" /></a>
      </div>
    </section>
    <footer class="about-thanks"><Icon name="Heart" :size="22" /><div><strong>谢谢你来，也谢谢你常来。</strong><p>希望每次见面，都能比上次顺手一点点。</p></div></footer>
    <OpenSourceLicenseModal :open="licenseOpen" :app-name="appName" :licenses="licenses" @close="licenseOpen = false" />
  </div>
</template>

<style scoped>
.about-message-logo { display:grid;place-items:center;width:76px;height:76px;flex-shrink:0;border-radius:22px;background:#e4edf8;color:#6484a7; }
.message-about { --muted:#78869a;--accent:#6683a5;--line:#e0e7ef; }
.message-about .section-heading { display:flex;align-items:center;justify-content:space-between;gap:12px;margin:28px 0 16px; }
.message-about .section-heading h2 { margin:0;font-size:20px; }
.message-about .section-heading .muted { font-size:12px; }
.message-about .icon-tile { display:grid;place-items:center;width:48px;height:48px;border-radius:16px;background:#edf3fa;color:#6683a5; }
.message-about .text-button { display:inline-flex;align-items:center;gap:8px;border:0;padding:0;background:none; }
.message-about .about-links a { text-decoration:none; }
.message-about .about-brand-card p,.message-about .about-thanks p { margin-bottom:0; }
@media(max-width:580px) { .message-about .section-heading { align-items:flex-start;flex-direction:column; }.about-message-logo { width:58px;height:58px; } }
.about-brand-card { display:flex;align-items:center;gap:22px;padding:32px;background:linear-gradient(120deg,#ffffffed,#edf5fa);border:1px solid white;border-radius:24px; }
.about-brand-card img { width:76px;height:76px; }
.about-brand-card h2 { font-size:30px;letter-spacing:-.04em;margin:5px 0 9px; }
.about-kicker { font-size:11px;color:#67889d; }
.about-brand-card p,.about-feature p,.about-links p,.about-thanks p { color:var(--muted);font-size:13px;line-height:1.8; }
.about-version { margin-left:auto;align-self:flex-start;border:1px solid #dae7ee;border-radius:20px;padding:6px 12px;color:#67889d;font-size:11px;white-space:nowrap; }
.about-feature-grid { display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:16px; }
.about-feature { display:flex;flex-direction:column;align-items:flex-start;padding:25px;background:#ffffffc9;border:1px solid white;border-radius:22px; }
.about-feature h3 { font-size:16px;margin:20px 0 10px; }
.about-feature p { margin-bottom:24px; }
.about-feature .text-button { margin-top:auto;font-size:12px;color:var(--accent);text-align:left; }
.about-links { display:grid;grid-template-columns:1fr 1fr;gap:16px; }
.about-links a { display:flex;align-items:center;gap:18px;padding:24px;background:#ffffffbc;border:1px solid white;border-radius:20px;transition:background .2s,transform .2s; }
.about-links a:hover { background:#edf5fb;transform:translateY(-2px); }
.about-links a>svg:first-child { color:inherit;flex-shrink:0; }
.about-links .bilibili { color:#c83c70;background:#fff1f6;border-color:#f6d6e3; }
.about-links .bilibili:hover { background:#ffe4ef; }
.about-links .github { color:#343a46;background:#eef0f4;border-color:#dce0e7; }
.about-links .github:hover { background:#e2e6ed; }
.about-links p { color:inherit;opacity:.75; }
.about-license { display:flex;align-items:center;gap:12px;margin:24px 0;padding:16px 20px;border:1px solid #ead7c4;border-radius:16px;background:#fff8ef;color:#8b4d21;font-weight:600;transition:background .2s; }
.about-license:hover { background:#f6e6d5; }
.about-license:focus-visible,.about-links a:focus-visible { outline:2px solid currentColor;outline-offset:4px; }
.about-links a>svg:last-child { margin-left:auto;flex-shrink:0; }
.about-links strong { font-size:14px; }
.about-links p { margin-top:4px; }
.about-thanks { display:flex;align-items:center;gap:16px;border-top:1px solid var(--line);margin-top:34px;padding:28px 4px; }
.about-thanks>svg { color:#b88794;flex-shrink:0; }
.about-thanks strong { font-size:14px; }
@media(max-width:1050px) { .about-feature-grid { grid-template-columns:1fr; }.about-feature { padding:24px; } }
@media(max-width:580px) { .about-brand-card { padding:24px;gap:16px;flex-wrap:wrap; }.about-brand-card img { width:58px;height:58px; }.about-brand-card h2 { font-size:25px; }.about-version { margin-left:0; }.about-links { grid-template-columns:1fr; } }
@media(prefers-reduced-motion:reduce) { .about-links a,.about-license { transition:none; } }
</style>
